import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { globalTheme } from "../../styles";
import { CarrierInput } from "./CarrierInput";
import { CarrierOptions } from "./CarrierOptions";
import { CarrierCheckBox } from "./CarrierCheckBox";
import { InternationalConsignmentDetails } from "./InternationalConsignmentDetails";
import { PrintByItemValidationModalContent } from "../PrintByItem/PrintByItemValidationModalContent";

interface Quote {
  parcelType: string;
  price: number;
  productId: string;
}

export const DeliveryRatesComparison = ({
  getQuoteResponse,
  orderId,
  CustomerDetails,
  customershipmentData,
  onValidation,
  onError,
  onClose,
}) => {
  const [carrierConfigs, setCarrierConfigs] = useState<any>([]);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);
  const [carrierRadioValues, setCarrierRadioValues] = useState<string>("");
  const [authtoleave, setauthtoleave] = useState(false);
  const [labelNumber, setLabelNumber] = useState("");
  const [allowPartialDelivery, setallowPartialDelivery] = useState(false);
  const [internationalConsignmentDetails, setInternationalConsignmentDetails] = useState<any>();
  const [openValidationsModal, setOpenValidationsModal] = useState<boolean>(false);

  const [shipmentData, setShipmentData] = useState<any>(null);

  const sku = customershipmentData.items.length > 0 ? customershipmentData.items[0].sku : null;

  const [formData, setFormData] = useState({
    shipmentAddress: CustomerDetails,
    shipmentItems: [],
    specialInstructions: "",
    myFastwayItemCode: "",
    transitCover: 0,
  });

  useEffect(() => {
    if (getQuoteResponse?.quotes) {
      const configs = Object.entries(getQuoteResponse.quotes).map(([carrierName, carrierData]) => ({
        carrierName,
        carrierData,
      }));
      setCarrierConfigs(configs);
    }
  }, [getQuoteResponse]);

  useEffect(() => {
    // Dynamically create shipment data whenever relevant dependencies change
    if (selectedQuote && selectedCarrier && carrierConfigs.length > 0) {
      const selectedCarrierConfig = carrierConfigs.find(({ carrierName }) => carrierName === selectedCarrier);

      if (selectedCarrierConfig) {
        const shipmentItems = selectedCarrierConfig.carrierData.items?.map((item) => ({
          reference: item.reference,
          quantity: item.quantity || "",
          weight: item.weight || "",
          length: item.length || "",
          width: item.width || "",
          height: item.height || "",
          parcelType: selectedQuote.parcelType,
          productId: selectedQuote.productId,
          price: selectedQuote.price,
          labelNumber: labelNumber,
          classificationType: CustomerDetails?.country !== "AU" ? "OTHER" : null,
          commercialValue:
            CustomerDetails?.country !== "AU" ? internationalConsignmentDetails?.consignmentForm?.[0]?.value : null,
          descriptionOfOther:
            CustomerDetails?.country !== "AU" ? internationalConsignmentDetails?.consignmentForm?.[4]?.value : null,
          exportDeclarationNumber:
            CustomerDetails?.country !== "AU" ? internationalConsignmentDetails?.consignmentForm?.[3]?.value || "" : null,
          importReferenceNumber:
            CustomerDetails?.country !== "AU" ? internationalConsignmentDetails?.consignmentForm?.[1]?.value : null,
          itemContents:
            CustomerDetails?.country !== "AU"
              ? [
                  {
                    countryOfOrigin: internationalConsignmentDetails?.itemsForm?.[0]?.value,
                    description: internationalConsignmentDetails?.itemsForm?.[5]?.value,
                    sku: sku,
                    quantity: internationalConsignmentDetails?.itemsForm?.[2]?.value,
                    tariffCode: internationalConsignmentDetails?.itemsForm?.[1]?.value || null,
                    value: internationalConsignmentDetails?.itemsForm?.[3]?.value,
                    weight: internationalConsignmentDetails?.itemsForm?.[4]?.value,
                    itemContentsReference: "",
                  },
                ]
              : null,
          itemDescription:
            CustomerDetails?.country !== "AU" ? internationalConsignmentDetails?.consignmentForm?.[2]?.value || "" : null,
        }));

        const createShipmentData = {
          orderId: parseInt(orderId),
          orderIds: customershipmentData?.orderIds,
          shipmentAddress: formData?.shipmentAddress,
          shipmentItems,
          shippingCarrier: selectedCarrier,
          specialInstructions: formData?.specialInstructions,
          authorityToLeave: CustomerDetails?.country === "AU" ? authtoleave : null,
          allowPartialDelivery: CustomerDetails?.country === "AU" ? allowPartialDelivery : null,
          transitCover: formData?.transitCover,
          myFastwayItemCode: carrierRadioValues,
        };

        setShipmentData(createShipmentData);
      }
    }
  }, [
    selectedQuote,
    selectedCarrier,
    carrierConfigs,
    formData,
    internationalConsignmentDetails,
    authtoleave,
    allowPartialDelivery,
    carrierRadioValues,
    labelNumber,
  ]);

  useEffect(() => {
    if (getQuoteResponse?.quotes) {
      const configs = Object.entries(getQuoteResponse.quotes).map(([carrierName, carrierData]) => ({
        carrierName,
        carrierData,
      }));
      setCarrierConfigs(configs);
    }
  }, [getQuoteResponse]);

  const handleQuoteClick = (quote: Quote, carrierName: string) => {
    setSelectedQuote(quote);
    setSelectedCarrier(carrierName);

    // Reset checkbox states when carrier option changes
    setauthtoleave(false);
    setallowPartialDelivery(false);
    setCarrierRadioValues("");
  };

  // Handle radio change for specific carriers
  const handleRadioChange = (event) => {
    const { value } = event.target;
    setCarrierRadioValues(value);
  };
  const handleInputChange = (event) => {
    const { value } = event.target;

    if (selectedCarrier === "INHOUSE") {
      setLabelNumber(value);
    }

    setCarrierConfigs((prevConfigs) =>
      prevConfigs.map((config) =>
        config.carrierName === selectedCarrier
          ? {
              ...config,
              carrierData: { ...config.carrierData, value },
            }
          : config
      )
    );

    setFormData((prevData) => ({
      ...prevData,
      shipmentItems: prevData.shipmentItems.map((item) =>
        item.reference === selectedCarrier ? { ...item, labelNumber: value } : item
      ),
      specialInstructions: value,
    }));
  };

  const handleCarrierCheckboxChange = (event, value: string) => {
    const isChecked = event.target.checked;

    if (value === "authority_to_leave") {
      setauthtoleave(isChecked);
    } else if (value === "partial_delivery") {
      setallowPartialDelivery(isChecked);
    }
  };

  // TODO: refactor these to consts/hooks
  const carrierCheckbox = {
    EPARCEL: [
      {
        key: "authority_to_leave",
        label: "Authority to leave",
        value: "authority_to_leave",
        checked: authtoleave,
      },
      {
        key: "partial_delivery",
        label: "Allow Partial delivery",
        value: "partial_delivery",
        checked: allowPartialDelivery,
      },
    ],
    DIRECTFREIGHT: [
      {
        key: "leave_at_door",
        label: "Leave At Door",
        value: "authority_to_leave",
        checked: authtoleave,
      },
    ],
  };

  // TODO: refactor these to consts/hooks
  const carrierRadioOptions = {
    MYFASTWAY: [
      { key: "leave_at_door", label: "Leave at Door", value: "ATL" },
      { key: "standard_service", label: "Standard Service", value: "STN" },
      { key: "signature_required", label: "Signature Required", value: "SGR" },
    ],
  };

  // TODO: refactor these to consts/hooks
  const carrierInputProps = {
    EPARCEL: {
      id: 0,
      label: "Cover Amount",
      variant: "standard",
      value: carrierConfigs.find((config) => config.carrierName === "EPARCEL")?.carrierData?.value || "",
    },
    MYFASTWAY: {
      id: 1,
      label: "Special Instructions",
      variant: "standard",
      value: carrierConfigs.find((config) => config.carrierName === "MYFASTWAY")?.carrierData?.value || "",
    },
    INHOUSE: {
      id: 2,
      label: "Label Number",
      variant: "standard",
      value: carrierConfigs.find((config) => config.carrierName === "INHOUSE")?.carrierData?.value || "",
    },
    DIRECTFREIGHT: {
      id: 3,
      label: "Special Instructions",
      variant: "standard",
      value: carrierConfigs.find((config) => config.carrierName === "DIRECTFREIGHT")?.carrierData?.value || "",
    },
  };

  // TODO: refactor these to consts
  const showCarrierInput = ["INHOUSE", "EPARCEL", "MYFASTWAY", "DIRECTFREIGHT"].includes(selectedCarrier || "");
  const showCarrierOptions = ["MYFASTWAY"].includes(selectedCarrier || "");
  const showCarrierCheckbox =
    (selectedCarrier === "EPARCEL" && CustomerDetails.country === "AU") || selectedCarrier === "DIRECTFREIGHT";

  const currentInputProps = carrierInputProps[selectedCarrier || ""] || {};
  const currentRadioOptions = carrierRadioOptions[selectedCarrier || ""] || [];
  const currentcheckbox = carrierCheckbox[selectedCarrier || ""] || [];

  const handleInternationalConsignmentDetailsSubmit = (InternationalConsignmentDetailsData) => {
    setInternationalConsignmentDetails(InternationalConsignmentDetailsData);
  };

  useEffect(() => {
    if (shipmentData) {
      setOpenValidationsModal(true);
    }
  }, [shipmentData]);

  return (
    <>
      <Stack direction="row" spacing={2} margin={1} padding={1}>
        {/* Carrier Cards */}
        <Stack spacing={2}>
          {carrierConfigs.length > 0 ? (
            carrierConfigs.map(({ carrierName, carrierData }) => (
              <Paper
                key={carrierName}
                elevation={3}
                sx={{ padding: 2, width: "300px", border: "10px solid", borderColor: globalTheme.border }}
              >
                <Typography variant="body1" sx={{ color: globalTheme.primaryColor, mb: 1 }}>
                  {carrierName}
                </Typography>
                <Divider />
                <Stack mt={1} spacing={1}>
                  {carrierData.carrierQuotes.length > 0 ? (
                    carrierData.carrierQuotes.map((quote: Quote, index: number) => (
                      <Box
                        key={index}
                        onClick={() => handleQuoteClick(quote, carrierName)}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          backgroundColor:
                            selectedQuote?.parcelType === quote?.parcelType ? globalTheme.secondaryColor : "inherit",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                          padding: 1,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">{quote.parcelType}</Typography>
                          <Typography variant="body2">${quote.price.toFixed(2)}</Typography>
                        </Stack>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">No quotes available for this carrier.</Typography>
                  )}
                </Stack>
              </Paper>
            ))
          ) : (
            <Typography variant="body2">No carriers available.</Typography>
          )}
        </Stack>

        {/* Quote Details */}

        <Stack sx={{ padding: 2 }}>
          {selectedQuote ? (
            <Paper elevation={3} sx={{ padding: 2, width: "500px", border: "10px solid", borderColor: globalTheme.border }}>
              <Typography mb={1} variant="h6" sx={{ color: globalTheme.primaryColor }}>
                Quote Details
              </Typography>
              <Divider />
              <Stack mt={1} spacing={1}>
                <Typography variant="body1">
                  <strong>Carrier:</strong> {selectedCarrier}
                </Typography>
                <Typography variant="body1">
                  <strong>Parcel Type:</strong> {selectedQuote.parcelType}
                </Typography>
                <Typography variant="body1">
                  <strong>Price:</strong> ${selectedQuote.price.toFixed(2)}
                </Typography>
              </Stack>
              {showCarrierInput && <CarrierInput {...currentInputProps} onChange={handleInputChange} />}
              {showCarrierOptions && (
                <CarrierOptions
                  options={currentRadioOptions}
                  selectedValue={carrierRadioValues} // Full string
                  onChange={handleRadioChange}
                />
              )}
              {showCarrierCheckbox && <CarrierCheckBox checkbox={currentcheckbox} onChange={handleCarrierCheckboxChange} />}

              {selectedCarrier === "EPARCEL" && CustomerDetails.country !== "AU" && (
                <InternationalConsignmentDetails handleSubmit={handleInternationalConsignmentDetailsSubmit} orderId={orderId} />
              )}
            </Paper>
          ) : (
            <Typography variant="body2">Select a quote to see details.</Typography>
          )}
        </Stack>
      </Stack>
      <Stack>
        {openValidationsModal && shipmentData && (
          <div style={{ marginBottom: "16px" }}>
            <Typography variant="h6" style={{ color: globalTheme.primaryColor }}>
              Validate Items and Quantity
            </Typography>
            <Box border={`1px solid ${globalTheme.border}`}>
              <PrintByItemValidationModalContent
                validatingItems={customershipmentData}
                onValidation={onValidation}
                onError={onError}
                onClose={onClose}
                manualShipmentsData={shipmentData}
                firstItemBarcode={null}
              />
            </Box>
          </div>
        )}
      </Stack>
      {/* <Button variant="contained" fullWidth onClick={handleCreateShipment} disabled={fetchingCreateShipmentRequest}>
        {fetchingCreateShipmentRequest ? <CircularProgress size={18} /> : "Create Shipment"}
      </Button> */}
    </>
  );
};
